import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import SingleEventEmitter from './singleEventEmitter';

const eventEmitterShape = PropTypes.shape({
  on: PropTypes.func.isRequired,
  off: PropTypes.func.isRequired,
  emit: PropTypes.func.isRequired
});

let contextInstanceCounter = 1;

/**
 * We implemented this because we wanted to take advantage of the fact that we support IE11+
 * And our event emitter can be implemented using `Map` which is much more efficient than `Array.indexOf` to find the callback when unmounting the component.
 * This API mimics the API introduced in React@16.3 and should be removed when we fully upgrade to it.
 */
export default defaultValue => {
  const eventEmitterSymbol = `$$__BIM360_Matrix_Context_Single_Event_Emitter_Symbol__Instance__${contextInstanceCounter}__$$`;
  contextInstanceCounter++;

  class Consumer extends PureComponent {
    constructor(props, context) {
      super(props);

      this.onValueChanged = this.onValueChanged.bind(this);

      let initialValue = defaultValue;
      if (context[eventEmitterSymbol]) {
        this.eventEmitter = context[eventEmitterSymbol];
        this.eventEmitter.on(this.onValueChanged);
        initialValue = this.eventEmitter.latest;
      }

      this.state = {
        value: initialValue
      };
    }

    componentWillUnmount() {
      if (this.eventEmitter) {
        this.eventEmitter.off(this.onValueChanged);
      }
    }

    onValueChanged(value) {
      this.setState({ value });
    }

    render() {
      return this.props.children(this.state.value);
    }
  }

  Consumer.contextTypes = {
    [eventEmitterSymbol]: eventEmitterShape
  };

  Consumer.propTypes = {
    children: PropTypes.func.isRequired
  };

  class Provider extends PureComponent {
    constructor(props) {
      super(props);
      // We are using an event emitter to be able to cause render on components even if a parent component declared `shouldComponentUpdate === false`.
      // This assumes that the first render will pass in the correct reference of eventEmitter in the context.
      // From then on we'll listen to changes using the event emitter.
      this.eventEmitter = new SingleEventEmitter(props.value);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
      if (nextProps.value !== this.props.value) {
        this.eventEmitter.emit(nextProps.value);
      }
    }

    getChildContext() {
      return { [eventEmitterSymbol]: this.eventEmitter };
    }

    render() {
      return this.props.children;
    }
  }

  Provider.childContextTypes = {
    [eventEmitterSymbol]: eventEmitterShape
  };

  Provider.defaultProps = {
    value: defaultValue
  };

  Provider.propTypes = {
    children: PropTypes.any,
    value: PropTypes.object
  };

  return {
    Consumer,
    Provider
  };
};
